import { renderMoment } from '@utils/timeUtils'
import { sourceLabel, subTypeLabel, statusLabel, isWin, freebetTag, clipId } from './transactionLabels'

// players.bets.data (+ echoed page/pageSize) -> render-ready view model.
// Everything the component needs is finished here; it only picks and renders.
export const toBetsView = (data, { renderValue }) => {
  const total = data?.total ?? 0
  const pageSize = data?.pageSize ?? 10
  const page = data?.page ?? 1

  // Multipliers / cashout are ratios, not money — always show 2 decimals.
  const toMultiplier = (value) =>
    renderValue(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  return {
    total,
    page,
    pageSize,
    pageCount: pageSize > 0 ? Math.ceil(total / pageSize) : 0,
    rows: (data?.bets ?? []).map((bet) => {
      // createdAt is UTC from BE -> convert to LOCAL, split into date + time.
      const moment = renderMoment({
        formatType: 'DD MMM YYYY', // "21 JUL 2026"
        utcDateString: bet.createdAt,
        options: { seconds: false, hourFormat: '24', displayMode: 'LOCAL' },
      })

      // Fully render-ready view model — cards render these directly, no logic.
      const tag = freebetTag({ id: bet.freebetId, source: bet.source })
      return {
        id: bet.transactionId, // stable key for rendering
        date: moment?.date ?? '', // "21 JUL 2026"
        time: moment?.time ?? '', // "14:00" (local)
        type: bet.type,
        subType: bet.subType,
        subTypeText: subTypeLabel(bet.subType), // "Freebet" / "Buy Bonus" …
        source: bet.source,
        sourceText: sourceLabel(bet.source), // "Custom" / "Rain" …
        // money values -> formatted with conventions, currency returned separately
        stake: renderValue(bet.betAmount),
        payout: bet.result === 'WON' ? `+ ${renderValue(bet.payout)}` : renderValue(bet.payout),
        payoutMultiplier: toMultiplier(bet.payoutMultiplier),
        crashPoint: toMultiplier(bet.crashMultiplier),
        status: bet.status,
        statusText: statusLabel(bet.status),
        freebetId: bet.freebetId,
        freebetAmount: renderValue(bet.freebetAmount),
        tag, // full freebet tag (tooltip)
        tagShort: clipId(tag), // clipped for display
        result: bet.result,
        won: isWin(bet.result), // green/red flag
        currency: bet.currency,
        cashoutAt: toMultiplier(bet.cashoutAt),
      }
    }),
  }
}

// Single bet detail (players.betDetails.data) -> render-ready view model, with the
// provably-fair block grouped for a verify panel.
export const toBetDetailsView = (data, { renderValue, formatDate }) => {
  if (!data) return null

  return {
    id: data.transactionId,
    roundId: data.roundId,
    stake: renderValue(data.betAmount),
    payout: renderValue(data.payout),
    multiplier: data.payoutMultiplier != null ? `${data.payoutMultiplier}x` : '—',
    crashPoint: data.crashMultiplier != null ? `${data.crashMultiplier}x` : '—',
    currency: data.currency,
    status: data.status,
    date: formatDate(data.createdAt),
    fairness: {
      clientSeed: data.clientSeed,
      hashedServerSeed: data.hashedServerSeed,
      nonce: data.nonce,
      hash: data.hash,
      seed: data.seed,
    },
  }
}
